import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ScrollArea } from "../components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  ClipboardCheck,
  Loader2,
  Search,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Calendar,
  Package,
  AlertTriangle,
  Filter,
  ChevronLeft,
  ChevronRight,
  Truck,
  Building2,
  FileText
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

const ESTADO_CONFIG = {
  pendiente: { label: "Pendiente", color: "bg-amber-100 text-amber-700 border-amber-200", icon: Clock },
  aprobada: { label: "Aprobada", color: "bg-blue-100 text-blue-700 border-blue-200", icon: CheckCircle },
  rechazada: { label: "Rechazada", color: "bg-red-100 text-red-700 border-red-200", icon: XCircle },
  parcial: { label: "Entrega Parcial", color: "bg-purple-100 text-purple-700 border-purple-200", icon: Package },
  entregada: { label: "Entregada", color: "bg-green-100 text-green-700 border-green-200", icon: CheckCircle },
  cancelada: { label: "Cancelada", color: "bg-slate-100 text-slate-700 border-slate-200", icon: XCircle }
};

const PRIORIDAD_CONFIG = {
  baja: { label: "Baja", color: "bg-slate-100 text-slate-600" },
  normal: { label: "Normal", color: "bg-blue-100 text-blue-600" },
  alta: { label: "Alta", color: "bg-orange-100 text-orange-600" },
  urgente: { label: "Urgente", color: "bg-red-100 text-red-600" }
};

export default function AprobarRequisicionPage() {
  const [requisiciones, setRequisiciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("pendientes");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const pageSize = 20;
  
  // Modal states
  const [selectedReq, setSelectedReq] = useState(null);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showDeliverDialog, setShowDeliverDialog] = useState(false);
  const [approvalData, setApprovalData] = useState({ aprobar: true, observaciones: "", items_aprobados: [] });
  const [deliveryData, setDeliveryData] = useState({ items_entrega: [] });
  const [processing, setProcessing] = useState(false);
  
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const { requisicionId: urlRequisicionId } = useParams();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("limit", pageSize);
      params.append("skip", page * pageSize);
      
      let estadoFilter = "";
      switch (activeTab) {
        case "pendientes": estadoFilter = "pendiente"; break;
        case "aprobadas": estadoFilter = "aprobada"; break;
        default: estadoFilter = "";
      }
      
      if (estadoFilter) {
        params.append("estado", estadoFilter);
      }

      const response = await axios.get(`${API}/requisiciones?${params.toString()}`, getAuthHeaders());
      setRequisiciones(response.data.requisiciones || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Error al cargar requisiciones");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, page, activeTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(0);
  }, [activeTab]);

  // Auto-open requisition from URL param (from email link)
  useEffect(() => {
    if (urlRequisicionId && requisiciones.length > 0) {
      const req = requisiciones.find(r => r.id === urlRequisicionId);
      if (req) {
        openApproveDialog(req);
      } else {
        // Fetch directly if not in current page
        const fetchAndOpen = async () => {
          try {
            const response = await axios.get(`${API}/requisiciones/${urlRequisicionId}`, getAuthHeaders());
            if (response.data) {
              setSelectedReq(response.data);
              const itemsAprobados = response.data.items?.map(item => ({
                articulo_id: item.articulo_id,
                cantidad_aprobada: item.cantidad_solicitada || item.cantidad
              })) || [];
              setApprovalData({ aprobar: true, observaciones: "", items_aprobados: itemsAprobados });
              setShowApproveDialog(true);
            }
          } catch (err) {
            console.error("Error loading requisición from URL:", err);
          }
        };
        fetchAndOpen();
      }
    }
  }, [urlRequisicionId, requisiciones]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    return new Date(timestamp).toLocaleDateString("es", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const openApproveDialog = async (req) => {
    try {
      // Fetch full details
      const response = await axios.get(`${API}/requisiciones/${req.id}`, getAuthHeaders());
      const fullReq = response.data;
      setSelectedReq(fullReq);
      
      // Initialize approval items with full requested quantity
      const itemsAprobados = fullReq.items?.map(item => ({
        articulo_id: item.articulo_id,
        cantidad_aprobada: item.cantidad_solicitada || item.cantidad
      })) || [];
      
      setApprovalData({ 
        aprobar: true, 
        observaciones: "",
        items_aprobados: itemsAprobados
      });
      setShowApproveDialog(true);
    } catch (err) {
      toast.error("Error al cargar detalles");
    }
  };

  const openDeliverDialog = async (req) => {
    try {
      console.log("Opening deliver dialog for req:", req.id);
      const response = await axios.get(`${API}/requisiciones/${req.id}`, getAuthHeaders());
      console.log("Requisicion details loaded:", response.data);
      const fullReq = response.data;
      setSelectedReq(fullReq);
      
      // Initialize delivery with pending amounts
      const itemsEntrega = fullReq.items?.map(item => ({
        articulo_id: item.articulo_id,
        cantidad: Math.max(0, (item.cantidad_aprobada || 0) - (item.cantidad_entregada || 0))
      })) || [];
      console.log("Items entrega initialized:", itemsEntrega);
      
      setDeliveryData({ items_entrega: itemsEntrega });
      setShowDeliverDialog(true);
      console.log("Dialog should be open now");
    } catch (err) {
      console.error("Error in openDeliverDialog:", err);
      console.error("Error response:", err.response?.data);
      toast.error(err.response?.data?.detail || "Error al cargar detalles de la requisición");
    }
  };

  const handleApprove = async () => {
    if (!selectedReq) return;
    
    try {
      setProcessing(true);
      await axios.post(
        `${API}/requisiciones/${selectedReq.id}/aprobar`,
        approvalData,
        getAuthHeaders()
      );
      
      toast.success(approvalData.aprobar ? "Requisición aprobada" : "Requisición rechazada");
      setShowApproveDialog(false);
      setSelectedReq(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al procesar");
    } finally {
      setProcessing(false);
    }
  };

  const handleDeliver = async () => {
    if (!selectedReq) return;
    
    // Filter out items with 0 quantity
    const itemsToDeliver = deliveryData.items_entrega.filter(i => i.cantidad > 0);
    if (itemsToDeliver.length === 0) {
      toast.error("Selecciona al menos un artículo para entregar");
      return;
    }
    
    try {
      setProcessing(true);
      await axios.post(
        `${API}/requisiciones/${selectedReq.id}/entregar`,
        { items_entrega: itemsToDeliver },
        getAuthHeaders()
      );
      
      toast.success("Entrega registrada exitosamente");
      setShowDeliverDialog(false);
      setSelectedReq(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar entrega");
    } finally {
      setProcessing(false);
    }
  };

  const updateApprovalQuantity = (articuloId, cantidad) => {
    setApprovalData(prev => ({
      ...prev,
      items_aprobados: prev.items_aprobados.map(item => 
        item.articulo_id === articuloId 
          ? { ...item, cantidad_aprobada: parseFloat(cantidad) || 0 }
          : item
      )
    }));
  };

  const updateDeliveryQuantity = (articuloId, cantidad) => {
    setDeliveryData(prev => ({
      ...prev,
      items_entrega: prev.items_entrega.map(item => 
        item.articulo_id === articuloId 
          ? { ...item, cantidad: parseFloat(cantidad) || 0 }
          : item
      )
    }));
  };

  const filteredRequisiciones = requisiciones.filter(req => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      req.numero?.toLowerCase().includes(search) ||
      req.solicitante_nombre?.toLowerCase().includes(search) ||
      req.motivo?.toLowerCase().includes(search)
    );
  });

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="aprobar-requisicion-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <ClipboardCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
                Aprobar Requisiciones
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Gestionar aprobaciones y entregas de materiales
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="pendientes" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Pendientes
            </TabsTrigger>
            <TabsTrigger value="aprobadas" className="flex items-center gap-2">
              <Truck className="w-4 h-4" />
              Por Entregar
            </TabsTrigger>
            <TabsTrigger value="historial" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Historial
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Search */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Buscar por número, solicitante..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <Card>
            <ScrollArea className="h-[calc(100vh-350px)]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número</TableHead>
                    <TableHead>Solicitante</TableHead>
                    <TableHead>Departamento</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Prioridad</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequisiciones.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12">
                        <ClipboardCheck className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                        <p className="text-slate-500">
                          {activeTab === "pendientes" 
                            ? "No hay requisiciones pendientes de aprobación" 
                            : activeTab === "aprobadas"
                            ? "No hay requisiciones pendientes de entrega"
                            : "No se encontraron requisiciones"}
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRequisiciones.map((req) => {
                      const estadoConfig = ESTADO_CONFIG[req.estado] || ESTADO_CONFIG.pendiente;
                      const prioridadConfig = PRIORIDAD_CONFIG[req.prioridad] || PRIORIDAD_CONFIG.normal;
                      const EstadoIcon = estadoConfig.icon;
                      
                      return (
                        <TableRow key={req.id} data-testid={`requisicion-row-${req.id}`}>
                          <TableCell className="font-mono font-medium">{req.numero}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-slate-400" />
                              {req.solicitante_nombre}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-slate-400" />
                              {req.departamento_nombre || "-"}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 text-sm">
                              <Calendar className="w-3 h-3 text-slate-400" />
                              {formatDate(req.created_at)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={prioridadConfig.color}>{prioridadConfig.label}</Badge>
                          </TableCell>
                          <TableCell>{req.items?.length || 0} items</TableCell>
                          <TableCell>
                            <Badge className={`${estadoConfig.color} flex items-center gap-1 w-fit`}>
                              <EstadoIcon className="w-3 h-3" />
                              {estadoConfig.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {req.estado === "pendiente" && (
                                <>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => openApproveDialog(req)}
                                    className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                    data-testid={`approve-btn-${req.id}`}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Revisar
                                  </Button>
                                </>
                              )}
                              {(req.estado === "aprobada" || req.estado === "parcial") && (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => openDeliverDialog(req)}
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  data-testid={`deliver-btn-${req.id}`}
                                >
                                  <Truck className="w-4 h-4 mr-1" />
                                  Entregar
                                </Button>
                              )}
                              {(req.estado === "entregada" || req.estado === "rechazada" || req.estado === "cancelada") && (
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  onClick={() => openApproveDialog(req)}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </ScrollArea>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t">
                <p className="text-sm text-slate-500">
                  Mostrando {page * pageSize + 1} - {Math.min((page + 1) * pageSize, total)} de {total}
                </p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm">Página {page + 1} de {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Approve/Reject Dialog */}
        <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5" />
                {selectedReq?.estado === "pendiente" ? "Revisar Requisición" : "Detalle de Requisición"} {selectedReq?.numero}
              </DialogTitle>
              <DialogDescription>
                {selectedReq?.estado === "pendiente" 
                  ? "Revisa los artículos solicitados y decide aprobar o rechazar"
                  : `Estado: ${ESTADO_CONFIG[selectedReq?.estado]?.label}`
                }
              </DialogDescription>
            </DialogHeader>
            
            {selectedReq && (
              <div className="space-y-4">
                {/* Request Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-xs text-slate-500">Solicitante</p>
                    <p className="font-medium">{selectedReq.solicitante_nombre}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Departamento</p>
                    <p className="font-medium">{selectedReq.departamento_nombre || "-"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Fecha Solicitud</p>
                    <p className="font-medium">{formatDate(selectedReq.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Fecha Requerida</p>
                    <p className="font-medium">{formatDate(selectedReq.fecha_requerida) || "-"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Prioridad</p>
                    <Badge className={PRIORIDAD_CONFIG[selectedReq.prioridad]?.color}>
                      {PRIORIDAD_CONFIG[selectedReq.prioridad]?.label}
                    </Badge>
                  </div>
                  <div className="col-span-3">
                    <p className="text-xs text-slate-500">Motivo</p>
                    <p className="font-medium text-sm">{selectedReq.motivo || "-"}</p>
                  </div>
                </div>

                {/* Items Table */}
                <div>
                  <p className="text-sm font-medium mb-2">Artículos Solicitados</p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Código</TableHead>
                        <TableHead>Artículo</TableHead>
                        <TableHead className="text-right">Solicitado</TableHead>
                        <TableHead className="text-right">Stock</TableHead>
                        {selectedReq.estado === "pendiente" && (
                          <TableHead className="text-right">Aprobar</TableHead>
                        )}
                        {selectedReq.estado !== "pendiente" && (
                          <>
                            <TableHead className="text-right">Aprobado</TableHead>
                            <TableHead className="text-right">Entregado</TableHead>
                          </>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedReq.items?.map((item, idx) => {
                        const stockBajo = item.articulo?.stock_actual < (item.cantidad_solicitada || item.cantidad);
                        const approvalItem = approvalData.items_aprobados?.find(i => i.articulo_id === item.articulo_id);
                        
                        return (
                          <TableRow key={idx}>
                            <TableCell className="font-mono text-sm">{item.articulo?.codigo}</TableCell>
                            <TableCell>
                              <div>
                                <p>{item.articulo?.nombre}</p>
                                {item.observaciones && (
                                  <p className="text-xs text-slate-500">{item.observaciones}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">{item.cantidad_solicitada || item.cantidad} {item.articulo?.unidad_medida}</TableCell>
                            <TableCell className="text-right">
                              <span className={stockBajo ? "text-red-600 font-medium" : ""}>
                                {item.articulo?.stock_actual || 0}
                              </span>
                              {stockBajo && (
                                <AlertTriangle className="w-3 h-3 inline ml-1 text-red-500" />
                              )}
                            </TableCell>
                            {selectedReq.estado === "pendiente" && (
                              <TableCell className="text-right">
                                <Input
                                  type="number"
                                  min={0}
                                  max={item.cantidad_solicitada || item.cantidad}
                                  value={approvalItem?.cantidad_aprobada ?? (item.cantidad_solicitada || item.cantidad)}
                                  onChange={(e) => updateApprovalQuantity(item.articulo_id, e.target.value)}
                                  className="w-20 text-right ml-auto"
                                  disabled={!approvalData.aprobar}
                                />
                              </TableCell>
                            )}
                            {selectedReq.estado !== "pendiente" && (
                              <>
                                <TableCell className="text-right text-blue-600">{item.cantidad_aprobada || 0}</TableCell>
                                <TableCell className="text-right text-green-600">{item.cantidad_entregada || 0}</TableCell>
                              </>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {/* Approval Decision - only for pending */}
                {selectedReq.estado === "pendiente" && (
                  <div className="space-y-4 pt-4 border-t">
                    <div className="flex gap-4">
                      <Button
                        variant={approvalData.aprobar ? "default" : "outline"}
                        className={approvalData.aprobar ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                        onClick={() => setApprovalData(prev => ({ ...prev, aprobar: true }))}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Aprobar
                      </Button>
                      <Button
                        variant={!approvalData.aprobar ? "destructive" : "outline"}
                        onClick={() => setApprovalData(prev => ({ ...prev, aprobar: false }))}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Rechazar
                      </Button>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium">Observaciones</label>
                      <Textarea
                        value={approvalData.observaciones}
                        onChange={(e) => setApprovalData(prev => ({ ...prev, observaciones: e.target.value }))}
                        placeholder={approvalData.aprobar ? "Observaciones de aprobación (opcional)" : "Motivo del rechazo (recomendado)"}
                        rows={2}
                      />
                    </div>
                  </div>
                )}

                {/* Approval Info - for already processed */}
                {selectedReq.estado !== "pendiente" && selectedReq.aprobador_nombre && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">
                      {selectedReq.estado === "rechazada" ? "Rechazado" : "Aprobado"} por: <span className="font-medium text-slate-700">{selectedReq.aprobador_nombre}</span> el {formatDate(selectedReq.fecha_aprobacion)}
                    </p>
                    {selectedReq.observaciones_aprobacion && (
                      <p className="text-sm mt-1">Observaciones: {selectedReq.observaciones_aprobacion}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowApproveDialog(false)}>
                {selectedReq?.estado === "pendiente" ? "Cancelar" : "Cerrar"}
              </Button>
              {selectedReq?.estado === "pendiente" && (
                <Button 
                  onClick={handleApprove} 
                  disabled={processing}
                  className={approvalData.aprobar ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}
                  data-testid="confirm-approval-btn"
                >
                  {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {approvalData.aprobar ? "Confirmar Aprobación" : "Confirmar Rechazo"}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Deliver Dialog */}
        <Dialog open={showDeliverDialog} onOpenChange={setShowDeliverDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5" />
                Registrar Entrega - {selectedReq?.numero}
              </DialogTitle>
              <DialogDescription>
                Indica las cantidades entregadas para cada artículo
              </DialogDescription>
            </DialogHeader>
            
            {selectedReq && (
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>Solicitante:</strong> {selectedReq.solicitante_nombre}
                  </p>
                  <p className="text-sm text-blue-700">
                    <strong>Departamento:</strong> {selectedReq.departamento_nombre || "-"}
                  </p>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Artículo</TableHead>
                      <TableHead className="text-right">Aprobado</TableHead>
                      <TableHead className="text-right">Ya Entregado</TableHead>
                      <TableHead className="text-right">Pendiente</TableHead>
                      <TableHead className="text-right">Entregar Ahora</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedReq.items?.map((item, idx) => {
                      const pendiente = (item.cantidad_aprobada || 0) - (item.cantidad_entregada || 0);
                      const deliveryItem = deliveryData.items_entrega?.find(i => i.articulo_id === item.articulo_id);
                      
                      return (
                        <TableRow key={idx}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{item.articulo?.nombre}</p>
                              <p className="text-xs text-slate-500 font-mono">{item.articulo?.codigo}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">{item.cantidad_aprobada || 0}</TableCell>
                          <TableCell className="text-right text-green-600">{item.cantidad_entregada || 0}</TableCell>
                          <TableCell className="text-right text-amber-600">{pendiente}</TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              min={0}
                              max={pendiente}
                              value={deliveryItem?.cantidad ?? 0}
                              onChange={(e) => updateDeliveryQuantity(item.articulo_id, e.target.value)}
                              className="w-20 text-right ml-auto"
                              disabled={pendiente <= 0}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeliverDialog(false)}>Cancelar</Button>
              <Button 
                onClick={handleDeliver} 
                disabled={processing}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="confirm-delivery-btn"
              >
                {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Registrar Entrega
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
